import { z } from "zod";

/**
 * PRTL-1375 — email addresses are treated as case-insensitive everywhere.
 *
 * A user who signed up as `LLacy@example.com` must be able to log in as
 * `llacy@example.com`, and a stray leading/trailing space pasted out of an
 * invite email must not produce "invalid email". The domain half of an
 * address is case-insensitive by RFC 1035; the local half technically is not,
 * but no mail provider we deal with actually distinguishes case, and treating
 * it as significant only ever produces lockouts.
 *
 * Normalization happens at every input boundary (this module) AND at the SQL
 * lookup (`findUserIdByEmail.sql`, which matches on `lower(btrim(email))`), so
 * legacy rows stored with mixed case still resolve.
 */
export function normalizeEmail(email: string): string {
    return email.trim().toLowerCase();
}

/**
 * Validating email input: normalizes first, then checks the format, so
 * `"  User@Example.com "` is accepted and stored as `user@example.com`.
 */
export const NormalizedEmail = z
    .string()
    .transform(normalizeEmail)
    .pipe(z.email("Invalid email address"));

/**
 * Credential-shaped email input (login, password reset). Deliberately does NOT
 * enforce the email format: these lookups return a generic "invalid email or
 * password", and a format error here would tell an unauthenticated caller
 * something about the address instead.
 */
export const CredentialEmail = z.string().transform(normalizeEmail);
